/**
 * The address a person asked for does not exist on this surface.
 *
 * An unknown path used to be rewritten to the landing page, so a typo, a stale
 * link, or a change that is no longer in this authority all arrived somewhere
 * else with the URL quietly replaced. Nothing said the address was wrong, and
 * the evidence of what had been asked for was gone.
 *
 * The 404 a drill-down route answers is not, on its own, evidence that the
 * authority does not hold the subject. Against the live authority
 * `GET /v1/surface/changes/{id}` answered 404 for the one id in
 * `recent_changes[0]`, and this page told the reader "This change is not in the
 * authority you are connected to" — which the same projection, on the same
 * screen, contradicts. Saying an authority does not hold something it is
 * visibly projecting is the render layer inventing authority state. What this
 * page may assert is what it can read: whether the projection it was drawn from
 * carries the subject, and that the detail route did not answer for it.
 */

import type { TemplateInstance } from 'hydrogen/runtime';
import { html } from 'hydrogen/runtime';

import type { ChangeRow } from '../generated/surface.js';
import { changeIdFromPath, PRODUCT_HOME_PATH, projectHref } from './nav.js';
import { Digest } from './product.js';
import { GLYPH, LinkButton } from './kit.js';
import { PublicFooter, PublicHeader } from './public-chrome.js';

/** Paths shaped like a change address that name nothing this authority holds. */
export function looksLikeChangeAddress(path: string): boolean {
  return changeIdFromPath(path) !== null;
}

/**
 * What the projection on screen says about the subject of the address.
 *
 * `address` is the state this page is in when nothing was looked up: a mistyped
 * path, or a change address reached before any projection arrived. The two
 * `change` arms are the two things a read of `recent_changes` can establish,
 * and neither of them is "this authority does not hold it".
 */
export type MissingSubject =
  | { kind: 'address' }
  | { kind: 'change'; carried: ChangeRow }
  | { kind: 'change'; carried: null; bounded: boolean };

const ADDRESS: MissingSubject = { kind: 'address' };

/** The change this projection carries under `id`, or null. */
export function carriedChange(
  changes: readonly ChangeRow[] | undefined,
  id: string,
): ChangeRow | null {
  return changes?.find((row) => row.id === id) ?? null;
}

/**
 * Whether the projection admits it lists only part of the certified history.
 *
 * `not_projected_rows` pushes a `recent_changes` row once the lineage is longer
 * than the snapshot's window, and its reason names the detail route as where
 * the rest is served. Where that row is present, a change missing from the list
 * is not a change missing from the authority, and this page must not say it is.
 */
export function boundedChangeList(gaps: readonly { id: string }[] | undefined): boolean {
  return gaps?.some((gap) => gap.id === 'recent_changes') ?? false;
}

function headline(subject: MissingSubject): string {
  if (subject.kind === 'address') return 'Nothing here. Yet forever.';
  return subject.carried
    ? 'This change is projected. Its detail is not.'
    : 'Nothing here. Yet forever.';
}

function kicker(subject: MissingSubject): string {
  return subject.kind === 'change' && subject.carried
    ? 'Detail not served'
    : 'Projection not found';
}

/**
 * The one sentence this page is allowed to assert, per arm.
 *
 * Each states a read that was actually performed. None of them concludes that
 * the authority does not hold the subject: a bounded projection cannot support
 * that, and a projection that lists the subject refutes it.
 */
function detail(subject: MissingSubject): string {
  if (subject.kind === 'address') {
    return "The page you're looking for doesn't exist in this projection. It may have been moved, deleted, or never written into the state.";
  }
  if (subject.carried) {
    return 'This authority is projecting this change — the change stream this page was drawn from lists it — and its detail route did not answer for it. What the projection carries is below; the mutations, evidence and certificate that only the detail route serves are not on this page.';
  }
  return subject.bounded
    ? 'The change stream this page was drawn from does not list this change, and the detail route that serves the rest did not answer for it. This projection carries only the most recent transitions, so an address missing from it has not been shown to be missing from the authority.'
    : 'The change stream this page was drawn from does not list this change, and the detail route did not answer for it. It may have been proposed against a different authority, or never certified into this state.';
}

/** The projected row, so the address answers with what the authority does hold. */
function Carried(row: ChangeRow): TemplateInstance {
  return html`
    <dl class="mx-not-found__carried" data-region="not-found-carried">
      <div><dt>Change</dt><dd>${Digest(row.id)}</dd></div>
      <div><dt>Producer</dt><dd>${row.producer}</dd></div>
      <div><dt>Intent</dt><dd>${row.intent}</dd></div>
      <div><dt>Base state</dt><dd>${Digest(row.base_state)}</dd></div>
      <div><dt>Mutations</dt><dd>${String(row.mutation_count)}</dd></div>
      <div><dt>Writes</dt><dd>${String(row.writes.length)}</dd></div>
      <div><dt>Claims</dt><dd>${String(row.claims.length)}</dd></div>
    </dl>
  `;
}

/**
 * Where to go next.
 *
 * "Browse changes" was the whole recovery on a change address, and on the live
 * authority that list held exactly one row: the link that had just failed. A
 * person who took it arrived back at the same 404. The stream stays reachable,
 * but it is never the only way out and never the primary one.
 */
function Actions(subject: MissingSubject, signedIn: boolean): TemplateInstance {
  if (!signedIn) {
    return html`${LinkButton({ href: '/', label: 'Go home', size: 'lg', iconEnd: GLYPH.arrowRight })}${LinkButton({ href: '/#how', label: 'See how it works', variant: 'outline', size: 'lg', spa: false, iconEnd: GLYPH.arrowRight })}`;
  }
  const carried = subject.kind === 'change' && subject.carried !== null;
  return html`${LinkButton({
    href: PRODUCT_HOME_PATH,
    label: carried ? 'See this change on the overview' : 'Go home',
    size: 'lg',
    iconEnd: GLYPH.arrowRight,
  })}${LinkButton({
    href: projectHref('changes'),
    label: carried ? 'Back to the change stream' : 'Browse changes',
    variant: 'outline',
    size: 'lg',
    iconEnd: GLYPH.arrowRight,
  })}`;
}

export function NotFoundBody(options: {
  path: string;
  signedIn: boolean;
  subject?: MissingSubject;
}): TemplateInstance {
  const subject = options.subject ?? ADDRESS;
  return html`
        <section class="mx-not-found" data-region="not-found" data-subject="${subject.kind}">
          <div>
            <p class="mx-not-found__kicker">
              <span aria-hidden="true">
                <svg viewBox="0 0 16 16" focusable="false">
                  <path fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" d="M8 2.4l5.8 10.2H2.2zM8 6.4v3M8 11.2h.01"></path>
                </svg>
              </span>
              ${kicker(subject)}
            </p>
            <p class="mx-not-found__code">404</p>
            <h1>${headline(subject)}</h1>
            <p class="mx-not-found__detail">${detail(subject)}</p>
            <p class="mx-not-found__path">
              <span>Requested</span><code>${options.path}</code>
            </p>
            ${subject.kind === 'change' && subject.carried ? Carried(subject.carried) : ''}
            <div class="mx-not-found__actions">${Actions(subject, options.signedIn)}</div>
            <div class="mx-not-found__rule" aria-hidden="true"><i></i><span>∞</span><i></i></div>
            <blockquote class="mx-not-found__quote">
              In a deterministic world, even errors are predictable. This one is part of the plan.
            </blockquote>
          </div>
        </section>
  `;
}

/** The signed-out refusal, wrapped in the public chrome that page belongs to. */
export function NotFoundPage(options: { path: string }): TemplateInstance {
  return html`
    <div class="mx-public" data-public-path="not-found">
      ${PublicHeader({ path: options.path })}
      <main class="mx-public-main" id="matrix-content" tabindex="-1">
        ${NotFoundBody({ path: options.path, signedIn: false })}
      </main>
      ${PublicFooter()}
    </div>
  `;
}
